import { useTranslation } from 'react-i18next';
import {
  Datatable,
  DatatableBody,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
  TooltipProvider,
} from '@monksflow/monks-ui';
import type { ProjectCampaign } from '@/entities/media-campaign';
import { PacingRow } from '../PacingRow';

export interface PacingTableProps {
  campaigns: ProjectCampaign[];
  isLoading: boolean;
}

/** Column heads in order, held as keys so the list stays a module constant unfrozen. */
const COLUMN_KEYS = [
  'performancePacing.pacingTable.campaign',
  'performancePacing.pacingTable.platform',
  'performancePacing.pacingTable.deliveredVsProjected',
  'performancePacing.pacingTable.pacing',
  'performancePacing.pacingTable.daysRemaining',
  'performancePacing.pacingTable.state',
] as const;

/**
 * One row per campaign, classifying and rendering the `pacingPct` the API already
 * computed. No pacing maths happens here or in `PacingRow` - see `pacing-status.ts`.
 */
export function PacingTable({ campaigns, isLoading }: PacingTableProps) {
  const { t } = useTranslation('projects');

  if (isLoading) {
    return <Skeleton className="h-40 w-full" data-testid="pacing-table-skeleton" />;
  }

  if (campaigns.length === 0) {
    return (
      <Empty data-testid="pacing-table-empty">
        <EmptyHeader>
          <EmptyTitle>{t('performancePacing.pacingTable.noCampaignsToPace')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    // One provider for the whole table, not one per row: the platform logos and the
    // pacing-unavailable pill both need one, and this app mounts none at its root.
    <TooltipProvider>
      <Datatable data-testid="pacing-table">
        <DatatableHeader>
          <DatatableRow>
            {COLUMN_KEYS.map((columnKey) => (
              <DatatableHead key={columnKey} scope="col">
                {t(columnKey)}
              </DatatableHead>
            ))}
          </DatatableRow>
        </DatatableHeader>
        <DatatableBody>
          {campaigns.map((campaign) => (
            <PacingRow key={campaign.id} campaign={campaign} />
          ))}
        </DatatableBody>
      </Datatable>
    </TooltipProvider>
  );
}
